/**
 * Turn Education Design System — Monochromatic
 *
 * All portals: white background, black/gray typography, Sora font.
 * No accent colors. No glassmorphism. Pill buttons. Flat cards.
 * Based on TURN-DESIGN-SPEC.md
 *
 * Usage:
 *   import { colors, glassStyles, getStatusClasses } from '@/lib/design';
 */

import type { CSSProperties } from 'react';

// =============================================================================
// COLOR TOKENS
// =============================================================================

export const colors = {
  brand: {
    primary: '#111111',
    primaryHover: '#333333',
    secondary: '#1A1A1A',
    accent: '#1A1A1A',
  },

  primary: {
    50: '#F5F5F5',
    100: '#E5E5E5',
    200: '#D4D4D4',
    300: '#A3A3A3',
    400: '#737373',
    500: '#1A1A1A',
    600: '#111111',
    700: '#000000',
    800: '#000000',
    900: '#000000',
  },

  accent: {
    blue: '#1A1A1A',
    green: '#34C759',
    red: '#FF3B30',
    orange: '#FF9500',
    yellow: '#FFD60A',
    teal: '#888888',
    indigo: '#1A1A1A',
    purple: '#1A1A1A',
    pink: '#1A1A1A',
  },

  status: {
    success: '#34C759',
    successBg: 'rgba(52, 199, 89, 0.12)',
    warning: '#FF9500',
    warningBg: 'rgba(255, 149, 0, 0.12)',
    error: '#FF3B30',
    errorBg: 'rgba(255, 59, 48, 0.12)',
    info: '#1A1A1A',
    infoBg: 'rgba(26, 26, 26, 0.05)',
  },

  severity: {
    critical: '#FF3B30',
    high: '#FF9500',
    medium: '#FFD60A',
    low: '#34C759',
    info: '#1A1A1A',
  },

  neutral: {
    0: '#FFFFFF',
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#E5E5E5',
    300: '#D4D4D4',
    400: '#A3A3A3',
    500: '#888888',
    600: '#3D3D3D',
    700: '#1A1A1A',
    800: '#111111',
    900: '#000000',
  },

  glass: {
    white80: 'rgba(255, 255, 255, 0.95)',
    white72: 'rgba(255, 255, 255, 0.90)',
    white65: 'rgba(255, 255, 255, 0.85)',
    white60: 'rgba(255, 255, 255, 0.80)',
    white40: 'rgba(255, 255, 255, 0.70)',
    stroke: '#D4D4D4',
    shadow: 'none',
  },

  text: {
    primary: '#1A1A1A',
    secondary: '#3D3D3D',
    tertiary: '#888888',
    disabled: '#D4D4D4',
    inverse: '#FFFFFF',
  },
} as const;

// =============================================================================
// GLASS EFFECT STYLES — Light Theme (Parent Portal)
// =============================================================================

export const glassStyles = {
  /** Card — flat, border only, no shadow */
  card: {
    background: '#FFFFFF',
    border: '1px solid #D4D4D4',
    borderRadius: '0',
  } as CSSProperties,

  cardElevated: {
    background: '#FFFFFF',
    border: '1px solid #D4D4D4',
    borderRadius: '0',
  } as CSSProperties,

  cardSubtle: {
    background: '#FFFFFF',
    border: '1px solid #E5E5E5',
    borderRadius: '0',
  } as CSSProperties,

  inset: {
    background: 'transparent',
    borderBottom: '1px solid #D4D4D4',
    borderRadius: '0',
  } as CSSProperties,

  header: {
    background: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(24px)',
    WebkitBackdropFilter: 'blur(24px)',
    borderBottom: '1px solid rgba(212, 212, 212, 0.3)',
  } as CSSProperties,

  sidebar: {
    background: '#FFFFFF',
    borderRight: '1px solid #E5E5E5',
  } as CSSProperties,

  modal: {
    background: '#FFFFFF',
    border: '1px solid #D4D4D4',
    borderRadius: '0',
  } as CSSProperties,

  accent: {
    background: 'rgba(0, 0, 0, 0.02)',
    border: '1px solid #D4D4D4',
    borderRadius: '0',
  } as CSSProperties,

  input: {
    background: 'transparent',
    border: 'none',
    borderBottom: '1px solid #D4D4D4',
    borderRadius: '0',
    color: '#1A1A1A',
  } as CSSProperties,

  buttonPrimary: {
    background: '#111111',
    border: 'none',
    color: '#FFFFFF',
    borderRadius: '100px',
  } as CSSProperties,

  buttonSecondary: {
    background: 'rgba(0, 0, 0, 0.05)',
    border: 'none',
    color: '#1A1A1A',
    borderRadius: '100px',
  } as CSSProperties,

  buttonGhost: {
    background: 'transparent',
    border: 'none',
    color: '#1A1A1A',
    borderRadius: '100px',
  } as CSSProperties,

  buttonDanger: {
    background: '#FF3B30',
    border: 'none',
    color: '#FFFFFF',
    borderRadius: '100px',
  } as CSSProperties,

  overlay: {
    background: 'rgba(0, 0, 0, 0.30)',
  } as CSSProperties,

  bottomSheet: {
    background: '#FFFFFF',
    borderTop: '1px solid #D4D4D4',
    borderRadius: '0',
  } as CSSProperties,
} as const;

// =============================================================================
// ADMIN (DARK) GLASS STYLES — used only inside .theme-admin
// =============================================================================

// Admin styles now match the same monochromatic system
export const adminGlassStyles = {
  card: {
    background: '#FFFFFF',
    border: '1px solid #D4D4D4',
    borderRadius: '0',
  } as CSSProperties,
  cardElevated: {
    background: '#FFFFFF',
    border: '1px solid #D4D4D4',
    borderRadius: '0',
  } as CSSProperties,
  cardSubtle: {
    background: '#FFFFFF',
    border: '1px solid #E5E5E5',
    borderRadius: '0',
  } as CSSProperties,
  header: {
    background: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(24px)',
    WebkitBackdropFilter: 'blur(24px)',
    borderBottom: '1px solid rgba(212, 212, 212, 0.3)',
  } as CSSProperties,
  sidebar: {
    background: '#FFFFFF',
    borderRight: '1px solid #E5E5E5',
  } as CSSProperties,
  modal: {
    background: '#FFFFFF',
    border: '1px solid #D4D4D4',
    borderRadius: '0',
  } as CSSProperties,
  input: {
    background: 'transparent',
    border: 'none',
    borderBottom: '1px solid #D4D4D4',
    borderRadius: '0',
    color: '#1A1A1A',
  } as CSSProperties,
  buttonPrimary: {
    background: '#111111',
    border: 'none',
    color: '#FFFFFF',
    borderRadius: '100px',
  } as CSSProperties,
  buttonSecondary: {
    background: 'rgba(0, 0, 0, 0.05)',
    border: 'none',
    color: '#1A1A1A',
    borderRadius: '100px',
  } as CSSProperties,
  buttonGhost: {
    background: 'transparent',
    border: 'none',
    color: '#1A1A1A',
    borderRadius: '100px',
  } as CSSProperties,
  buttonDanger: {
    background: '#FF3B30',
    border: 'none',
    color: '#FFFFFF',
    borderRadius: '12px',
  } as CSSProperties,
  overlay: {
    background: 'rgba(0, 0, 0, 0.60)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
  } as CSSProperties,
  bottomSheet: {
    background: 'rgba(28, 28, 30, 0.98)',
    backdropFilter: 'blur(48px) saturate(2)',
    WebkitBackdropFilter: 'blur(48px) saturate(2)',
    borderTop: '1px solid rgba(255, 255, 255, 0.10)',
    borderRadius: '24px 24px 0 0',
    boxShadow: '0 -8px 32px rgba(0, 0, 0, 0.50)',
  } as CSSProperties,
} as const;

// =============================================================================
// SHADOWS
// =============================================================================

export const shadows = {
  sm: '0 2px 4px rgba(0, 0, 0, 0.04)',
  md: '0 4px 16px rgba(0, 0, 0, 0.06)',
  lg: '0 8px 32px rgba(92, 45, 145, 0.05)',
  xl: '0 16px 48px rgba(92, 45, 145, 0.08)',
  '2xl': '0 24px 80px rgba(92, 45, 145, 0.12)',
  glass: '0 8px 32px rgba(92, 45, 145, 0.05), 0 1px 3px rgba(0, 0, 0, 0.04)',
  glow: (color: string, opacity = 0.3) => `0 0 32px rgba(${color}, ${opacity})`,
  glowPurple: '0 0 32px rgba(92, 45, 145, 0.30)',
  glowGreen: '0 0 32px rgba(52, 199, 89, 0.30)',
  glowRed: '0 0 32px rgba(255, 59, 48, 0.30)',
  inset: 'inset 0 1px 0 rgba(255, 255, 255, 0.85)',
} as const;

// =============================================================================
// TYPOGRAPHY
// =============================================================================

export const typography = {
  fontFamily: {
    display: "'Roboto Slab', 'Rockwell', serif",
    body: "'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    mono: "'Overpass Mono', 'SF Mono', 'JetBrains Mono', monospace",
    turn: "'Comic Sans MS', 'Comic Sans', 'Chalkboard SE', cursive",
  },
  fontSize: {
    xs: '0.75rem',
    sm: '0.875rem',
    base: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
    '2xl': '1.5rem',
    '3xl': '1.875rem',
    '4xl': '2.25rem',
    '5xl': '3rem',
  },
  fontWeight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeight: {
    tight: 1.2,
    normal: 1.5,
    relaxed: 1.625,
  },
  letterSpacing: {
    tight: '-0.02em',
    normal: '0',
    wide: '0.02em',
  },
} as const;

// =============================================================================
// GRADIENTS
// =============================================================================

export const gradients = {
  appBg: '#FFFFFF',
  appBgSubtle: '#FFFFFF',
  appBgRadial: '#FFFFFF',
  adminBg: '#FFFFFF',
  accentPurple: '#111111',
  accentGreen: '#111111',
  surfaceElevated: '#FFFFFF',
  rainbow: 'linear-gradient(90deg, #1A1A1A, #888888, #D4D4D4)',
} as const;

// =============================================================================
// SPACING
// =============================================================================

export const spacing = {
  xs: '0.25rem',
  sm: '0.5rem',
  md: '1rem',
  lg: '1.5rem',
  xl: '2rem',
  '2xl': '3rem',
  '3xl': '4rem',
  '4xl': '6rem',
} as const;

// =============================================================================
// BORDER RADIUS
// =============================================================================

export const borderRadius = {
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '20px',
  '2xl': '24px',
  full: '9999px',
} as const;

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

type StatusType = 'success' | 'warning' | 'error' | 'info';

export function getStatusClasses(status: StatusType): string {
  const map: Record<StatusType, string> = {
    success: 'bg-[#34C759]/12 text-[#34C759] border-[#34C759]/25',
    warning: 'bg-[#FF9500]/12 text-[#FF9500] border-[#FF9500]/25',
    error: 'bg-[#FF3B30]/12 text-[#FF3B30] border-[#FF3B30]/25',
    info: 'bg-[#111111]/8 text-[#1A1A1A] border-[#1A1A1A]/20',
  };
  return `inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${map[status]}`;
}

type SeverityType = 'critical' | 'high' | 'medium' | 'low' | 'info';

export function getSeverityClasses(severity: SeverityType): string {
  const map: Record<SeverityType, string> = {
    critical: 'bg-[#FF3B30]/12 text-[#FF3B30] border-[#FF3B30]/25',
    high: 'bg-[#FF9500]/12 text-[#FF9500] border-[#FF9500]/25',
    medium: 'bg-[#FFD60A]/12 text-[#FFD60A] border-[#FFD60A]/25',
    low: 'bg-[#34C759]/12 text-[#34C759] border-[#34C759]/25',
    info: 'bg-[#111111]/8 text-[#1A1A1A] border-[#1A1A1A]/20',
  };
  return `inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${map[severity]}`;
}

export function getStatusColor(status: StatusType): string {
  return colors.status[status];
}

export function getSeverityColor(severity: SeverityType): string {
  return colors.severity[severity];
}

// =============================================================================
// CSS CLASS MAPPINGS
// =============================================================================

export const cx = {
  card: 'glass rounded-2xl',
  cardElevated: 'glass-elevated rounded-[20px]',
  cardSubtle: 'glass-subtle rounded-xl',

  btnPrimary: 'px-6 py-3 bg-[#111111] hover:bg-[#4A2476] active:scale-[0.98] text-white font-medium rounded-xl transition-all focus:ring-2 focus:ring-[#1A1A1A]/40 focus:ring-offset-2 focus:ring-offset-[#FFFFFF]',
  btnSecondary: 'px-6 py-3 bg-white/60 hover:bg-white/75 text-[#1C1C1E] font-medium rounded-xl border border-black/8 transition-all focus:ring-2 focus:ring-[#1A1A1A]/20 focus:ring-offset-2 focus:ring-offset-[#FFFFFF]',
  btnGhost: 'px-6 py-3 text-[#1A1A1A] hover:bg-[#111111]/5 font-medium rounded-xl transition-all',
  btnDanger: 'px-6 py-3 bg-[#FF3B30] hover:brightness-110 active:scale-[0.98] text-white font-medium rounded-xl transition-all focus:ring-2 focus:ring-[#FF3B30]/40 focus:ring-offset-2 focus:ring-offset-[#FFFFFF]',
  btnIcon: 'p-3 hover:bg-black/5 rounded-xl transition-all min-w-[44px] min-h-[44px] flex items-center justify-center text-[#636366] hover:text-[#1C1C1E]',

  input: 'w-full px-4 py-3 bg-black/[0.03] border border-black/[0.08] rounded-xl text-[#1C1C1E] placeholder-[#8E8E93] focus:border-[#1A1A1A]/40 focus:ring-2 focus:ring-[#1A1A1A]/15 transition-all',
  textarea: 'w-full px-4 py-3 bg-black/[0.03] border border-black/[0.08] rounded-xl text-[#1C1C1E] placeholder-[#8E8E93] focus:border-[#1A1A1A]/40 focus:ring-2 focus:ring-[#1A1A1A]/15 transition-all resize-none',

  pageContainer: 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8',
  sectionTitle: 'text-xl font-semibold text-[#1C1C1E]',
  sectionSubtitle: 'text-sm text-[#636366] mt-1',

  statusDot: (status: StatusType) => {
    const colorMap: Record<StatusType, string> = {
      success: 'bg-[#34C759]',
      warning: 'bg-[#FF9500]',
      error: 'bg-[#FF3B30]',
      info: 'bg-[#111111]',
    };
    return `w-2 h-2 rounded-full ${colorMap[status]}`;
  },

  textPrimary: 'text-[#1C1C1E]',
  textSecondary: 'text-[#636366]',
  textTertiary: 'text-[#8E8E93]',
  textDisabled: 'text-[#C7C7CC]',

  bgBase: 'bg-white',
  bgElevated1: 'bg-white',
  bgElevated2: 'bg-[#F5F1EB]',
  bgElevated3: 'bg-[#EDE8E0]',
  bgElevated4: 'bg-[#D6D0C8]',
} as const;

// =============================================================================
// ANIMATION TOKENS
// =============================================================================

export const animation = {
  duration: {
    fast: '150ms',
    normal: '200ms',
    slow: '300ms',
    slower: '500ms',
  },
  easing: {
    default: 'cubic-bezier(0.4, 0, 0.2, 1)',
    in: 'cubic-bezier(0.4, 0, 1, 1)',
    out: 'cubic-bezier(0, 0, 0.2, 1)',
    inOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    spring: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  },
} as const;
